import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Slider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import { getScoreDescription, getShortScoreDescription, getScoreColor as getScoreColorUtil } from '../../utils/scoreDescription';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { extractErrorMessage } from '../../utils/errorHandler';
import { filterExcludedEvaluationItems } from '../../utils/excludedEvaluationItems';
import { GOAL_SETTING_PATH } from '../../utils/routes';
import { ItemGoalReference } from '../../components/Evaluation/ItemGoalReference';

// ==============================
// 型定義
// ==============================

interface User {
  employee_id: string;
  name: string;
  email: string;
  department: string;
  department_type: 'tech' | 'sales' | 'admin';
  job_grade: string;
  job_title: string;
  rank: string;
  roles: string[];
}

interface EvaluationItem {
  item_id: string;
  department_type: string;
  category_number: number;
  category_name: string;
  item_number: string;
  item_description: string;
  weight: number;
  display_order: number;
  detailed_definition?: string;
  evaluation_points?: string[];
  /** 確定した項目セットから来た場合に入る、その項目の目標 */
  goal_target_description?: string | null;
  goal_achievement_criteria?: string | null;
  requires_goal?: boolean;
  is_free_item?: boolean;
}

interface EvaluationPeriod {
  period_id: string;
  period_name: string;
  self_eval_start: string;
  self_eval_end: string;
  status: string;
}

interface EvaluationItemInput {
  target_description: string;
  achievement_criteria: string;
  first_evaluation_score: number;
  first_evaluation_comment: string;
}

interface EvaluatorItemScore {
  evaluator_id: string;
  evaluator_name: string;
  evaluation_category: 'manager' | 'dept_head';
  evaluation_weight: number;
  item_id: string;
  evaluation_score: number | null;
  evaluation_comment: string | null;
}

// ==============================
// メインコンポーネント
// ==============================

const SelfEvaluationPage: React.FC = () => {
  const { periodStatus } = useAuth();
  // ステート管理
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [evaluationPeriods, setEvaluationPeriods] = useState<EvaluationPeriod[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [evaluationItems, setEvaluationItems] = useState<EvaluationItem[]>([]);
  // マスタ側の項目取得が終わったか。確定した項目セットで上書きするため、
  // 取得完了の判定を evaluationItems の件数に依存させない
  const [masterItemsLoaded, setMasterItemsLoaded] = useState(false);
  const [formData, setFormData] = useState<{ [key: string]: EvaluationItemInput }>({});
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [evaluationStatus, setEvaluationStatus] = useState<string | null>(null);
  const [evaluatorItemScores, setEvaluatorItemScores] = useState<EvaluatorItemScore[]>([]);
  const [approvedGoal, setApprovedGoal] = useState<{
    target_description: string;
    achievement_criteria: string;
    approval_status: string;
  } | null>(null);
  const [goalsApproved, setGoalsApproved] = useState(false);
  const [evaluationId, setEvaluationId] = useState<string | null>(null);
  const [latestRejectionComment, setLatestRejectionComment] = useState<string | null>(null);
  const [allowOutOfPeriod, setAllowOutOfPeriod] = useState(false);

  // ==============================
  // データ取得
  // ==============================

  useEffect(() => {
    fetchUserData();
    fetchEvaluationPeriods();
  }, []);

  useEffect(() => {
    if (user && user.department_type) {
      fetchEvaluationItems(user.department_type);
    }
  }, [user]);

  // 評価期間が変更されたら保存済みデータを読み込む
  useEffect(() => {
    if (selectedPeriod && masterItemsLoaded) {
      fetchSavedDraft(selectedPeriod);
      fetchEvaluationGoal(selectedPeriod);
    }
  }, [selectedPeriod, masterItemsLoaded]);

  const fetchEvaluationGoal = async (periodId: string) => {
    try {
      const response = await api.get(`/evaluation-goals/period/${periodId}`);
      const goal = response.data.data?.goal;
      setAllowOutOfPeriod(response.data.data?.allowOutOfPeriodEdit === true);
      if (goal?.approval_status === 'approved') {
        setApprovedGoal(goal);
        setGoalsApproved(true);
      } else {
        setApprovedGoal(goal);
        setGoalsApproved(false);
      }
    } catch {
      setApprovedGoal(null);
      setGoalsApproved(false);
    }
  };

  const fetchUserData = async () => {
    try {
      const response = await api.get('/auth/me');

      if (response.data.success) {
        setUser(response.data.data);
      }
    } catch (error) {
      console.error('ユーザー情報取得エラー:', error);
      toast.error(extractErrorMessage(error, 'ユーザー情報の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluationPeriods = async () => {
    try {
      const response = await api.get('/evaluation-periods');

      if (response.data.success) {
        const periods = response.data.data || [];
        setEvaluationPeriods(periods);

        // デフォルトで最初の評価期間を選択
        if (periods.length > 0) {
          setSelectedPeriod(periods[0].period_id);
        }
      }
    } catch (error) {
      console.error('評価期間取得エラー:', error);
      toast.error(extractErrorMessage(error, '評価期間の取得に失敗しました'));
    }
  };

  const fetchEvaluationItems = async (departmentType: string) => {
    try {
      const response = await api.get(
        `/evaluation-items/by-department/${departmentType}`
      );

      if (response.data.success) {
        const items = filterExcludedEvaluationItems(
          response.data.data.categories.flatMap((cat: any) => cat.items)
        ) as EvaluationItem[];
        setEvaluationItems(items);

        // フォームデータを初期化
        const initialFormData: { [key: string]: EvaluationItemInput } = {};
        items.forEach((item: EvaluationItem) => {
          initialFormData[item.item_id] = {
            target_description: '',
            achievement_criteria: '',
            first_evaluation_score: 6,
            first_evaluation_comment: '',
          };
        });
        setFormData(initialFormData);
      }
    } catch (error) {
      console.error('評価項目取得エラー:', error);
      toast.error(extractErrorMessage(error, '評価項目の取得に失敗しました'));
    } finally {
      setMasterItemsLoaded(true);
    }
  };

  const fetchSavedDraft = async (periodId: string) => {
    try {
      const response = await api.get(
        `/self-evaluation/draft/${periodId}`
      );


      if (response.data.success && response.data.data) {
        const savedData = response.data.data;

        // 評価ステータスを保存
        if (savedData.evaluation) {
          if (savedData.evaluation.status) {
            setEvaluationStatus(savedData.evaluation.status);
          }
          if (savedData.evaluation.evaluation_id) {
            setEvaluationId(savedData.evaluation.evaluation_id);
            try {
              const histRes = await api.get(
                `/evaluations/${savedData.evaluation.evaluation_id}/rejection-history`
              );
              const latest = histRes.data.data?.[0];
              if (latest && latest.rejected_to_status === 'goal_setting') {
                setLatestRejectionComment(latest.rejection_comment || null);
              } else {
                setLatestRejectionComment(null);
              }
            } catch {
              setLatestRejectionComment(null);
            }
          }
        }

        // 評価者スコアを保存 (上長評価・部門長評価)
        if (savedData.evaluatorItemScores) {
          setEvaluatorItemScores(savedData.evaluatorItemScores);
        }

        // バックエンドのレスポンスは items フィールドを使用
        const savedItems = savedData.items || savedData.evaluation_items;


        // 保存済みデータが存在する場合、フォームデータを更新
        if (savedItems && savedItems.length > 0) {
          // 確定した項目セット（evaluation_items）を正とする。
          // マスタに後から項目が増えても、この人の評価対象は変わらない（設計 §7.7）
          setEvaluationItems(
            savedItems.map((savedItem: any) => ({
              item_id: savedItem.item_id,
              department_type: user?.department_type || '',
              category_number: savedItem.category_number,
              category_name: savedItem.category_name,
              item_number: savedItem.item_number,
              item_description: savedItem.item_description,
              weight: Number(savedItem.weight),
              display_order: savedItem.display_order ?? 0,
              detailed_definition: savedItem.detailed_definition || undefined,
              evaluation_points: savedItem.evaluation_points || undefined,
              goal_target_description: savedItem.target_description,
              goal_achievement_criteria: savedItem.achievement_criteria,
              requires_goal: savedItem.requires_goal,
              is_free_item: savedItem.is_free_item,
            }))
          );

          const updatedFormData: { [key: string]: EvaluationItemInput } = { ...formData };

          savedItems.forEach((savedItem: any) => {
            updatedFormData[savedItem.item_id] = {
              target_description: savedItem.target_description || '',
              achievement_criteria: savedItem.achievement_criteria || '',
              first_evaluation_score: savedItem.first_evaluation_score || 6,
              first_evaluation_comment: savedItem.first_evaluation_comment || '',
            };
          });

          setFormData(updatedFormData);
        }
      }
    } catch (error) {
      // 404エラーは保存済みデータがない場合なので、エラー表示しない
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any).response?.status !== 404) {
        console.error('保存済みデータ取得エラー:', error);
      }
    }
  };

  // ==============================
  // フォーム操作
  // ==============================

  const handleInputChange = (itemId: string, field: keyof EvaluationItemInput, value: any) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [itemId]: {
          ...prev[itemId],
          [field]: value,
        },
      };
      return updated;
    });
  };

  const mapFormToEvaluationItems = () =>
    Object.entries(formData).map(([item_id, data]) => ({ item_id, ...data }));

  const handleSaveDraft = async () => {
    if (!selectedPeriod) {
      toast.error('評価期間を選択してください');
      return;
    }

    setSaving(true);
    try {
      await api.post('/self-evaluation/draft', {
        period_id: selectedPeriod,
        evaluation_items: mapFormToEvaluationItems(),
      });

      toast.success('評価を一時保存しました');

      // 保存後、データを再読み込みして確認
      await fetchSavedDraft(selectedPeriod);
    } catch (error) {
      console.error('一時保存エラー:', error);
      toast.error(extractErrorMessage(error, '一時保存に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    if (!goalsApproved) {
      toast.error('目標が上長承認済みになるまで、自己評価を提出できません');
      return;
    }
    setSubmitDialogOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!selectedPeriod) {
      toast.error('評価期間を選択してください');
      return;
    }

    setSaving(true);
    try {
      await api.post('/self-evaluation/draft', {
        period_id: selectedPeriod,
        evaluation_items: mapFormToEvaluationItems(),
      });

      // Then submit
      await api.post(
        '/self-evaluation/submit',
        {
          period_id: selectedPeriod,
        }
      );

      toast.success('自己評価を提出しました');
      setSubmitDialogOpen(false);

      // ステータスを更新
      setEvaluationStatus('self_evaluated');

      // データを再読み込み
      await fetchSavedDraft(selectedPeriod);
    } catch (error) {
      console.error('提出エラー:', error);
      toast.error(extractErrorMessage(error, '提出に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  // ==============================
  // ヘルパー関数
  // ==============================

  // カテゴリ別にグループ化
  const groupedItems = evaluationItems.reduce((acc, item) => {
    const key = `${item.category_number}-${item.category_name}`;
    if (!acc[key]) {
      acc[key] = {
        category_number: item.category_number,
        category_name: item.category_name,
        items: [],
      };
    }
    acc[key].items.push(item);
    return acc;
  }, {} as { [key: string]: { category_number: number; category_name: string; items: EvaluationItem[] } });

  const categories = Object.values(groupedItems).sort((a, b) => a.category_number - b.category_number);

  // 現ステータスで確定している評価のみ表示（差し戻し後の未確定評価は出さない）
  const managerEvalConfirmed =
    !['root_defined', 'goal_setting', 'self_evaluated'].includes(evaluationStatus || '');
  const deptHeadEvalConfirmed =
    !['root_defined', 'goal_setting', 'self_evaluated', 'manager_evaluated'].includes(
      evaluationStatus || ''
    );

  // 上長評価者の一覧を取得（重複除外）
  const getManagerEvaluators = () => {
    if (!managerEvalConfirmed) return [];
    const managers = evaluatorItemScores.filter(
      (score) => score.evaluation_category === 'manager'
    );
    const uniqueEvaluators = Array.from(
      new Map(managers.map((m) => [m.evaluator_id, m])).values()
    );
    return uniqueEvaluators;
  };

  // 部門長評価者の一覧を取得（重複除外）
  const getDeptHeadEvaluators = () => {
    if (!deptHeadEvalConfirmed) return [];
    const deptHeads = evaluatorItemScores.filter(
      (score) => score.evaluation_category === 'dept_head'
    );
    const uniqueEvaluators = Array.from(
      new Map(deptHeads.map((d) => [d.evaluator_id, d])).values()
    );
    return uniqueEvaluators;
  };

  // 評価者の特定項目に対するスコアを取得
  const getEvaluatorScore = (evaluatorId: string, itemId: string): number | null => {
    const score = evaluatorItemScores.find(
      (s) => s.evaluator_id === evaluatorId && s.item_id === itemId
    );
    return score?.evaluation_score ?? null;
  };

  // 評価者の特定項目に対するコメントを取得
  const getEvaluatorComment = (evaluatorId: string, itemId: string): string | null => {
    const score = evaluatorItemScores.find(
      (s) => s.evaluator_id === evaluatorId && s.item_id === itemId
    );
    return score?.evaluation_comment ?? null;
  };

  const isActivePeriod =
    !selectedPeriod || selectedPeriod === periodStatus?.activePeriodId;
  const canEditInPeriod =
    isActivePeriod && ((periodStatus?.canSubmitSelfEvaluation ?? true) || allowOutOfPeriod);
  const canEditEvaluation =
    goalsApproved &&
    canEditInPeriod &&
    (evaluationStatus === null ||
      evaluationStatus === 'root_defined' ||
      evaluationStatus === 'goal_setting');

  const isSubmitted = !canEditEvaluation;

  // ==============================
  // レンダリング
  // ==============================

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">ユーザー情報が取得できません。再ログインしてください。</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* ヘッダー */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          自己評価
        </Typography>
        <Typography color="text.secondary">
          各評価項目について、自己評価点とコメントを入力してください。目標は「目標設定」画面で入力・承認済みの内容が適用されます。
        </Typography>
      </Box>

      {latestRejectionComment && canEditEvaluation && (
        <Alert severity="info" sx={{ mb: 3 }}>
          評価が差し戻されました。以前の入力内容を確認のうえ修正してください。
          <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
            {latestRejectionComment}
          </Typography>
        </Alert>
      )}

      {!goalsApproved && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          自己評価を入力するには、先に
          <Link component={RouterLink} to={GOAL_SETTING_PATH} sx={{ mx: 0.5 }}>
            目標設定
          </Link>
          で目標を登録し、上長の承認を受けてください。
          {approvedGoal?.approval_status === 'pending_approval' && '（現在、承認待ちです）'}
          {approvedGoal?.approval_status === 'rejected' && '（差し戻しされています）'}
        </Alert>
      )}

      {/* ユーザー情報 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              社員ID
            </Typography>
            <Typography variant="body1">{user.employee_id}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              氏名
            </Typography>
            <Typography variant="body1">{user.name}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              部門
            </Typography>
            <Typography variant="body1">{user.department}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              職位
            </Typography>
            <Typography variant="body1">{user.job_title}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* 評価期間選択 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>評価期間</InputLabel>
          <Select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            label="評価期間"
          >
            {evaluationPeriods.map((period) => (
              <MenuItem key={period.period_id} value={period.period_id}>
                {period.period_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* 評価項目 */}
      {categories.length === 0 ? (
        <Alert severity="info">評価項目が見つかりません。</Alert>
      ) : (
        categories.map((category) => (
          <Accordion key={`${category.category_number}-${category.category_name}`} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                {category.category_number}. {category.category_name}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {category.items.map((item) => (
                  <Card key={item.item_id} sx={{ mb: 3 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Box flex={1}>
                          <Typography variant="subtitle1" gutterBottom>
                            {item.item_number}: {item.item_description}
                          </Typography>
                          <Box display="flex" gap={1} alignItems="center">
                            <Chip label={`重み: ${item.weight}点`} size="small" color="primary" />
                          </Box>
                        </Box>
                      </Box>

                      {/* 評価項目の定義・評価ポイント */}
                      {(item.detailed_definition || (item.evaluation_points && item.evaluation_points.length > 0)) && (
                        <Box mb={2} sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                          {item.detailed_definition && (
                            <Box mb={1}>
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                評価項目の定義：
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {item.detailed_definition}
                              </Typography>
                            </Box>
                          )}
                          {item.evaluation_points && item.evaluation_points.length > 0 && (
                            <Box>
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                評価のポイント：
                              </Typography>
                              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {item.evaluation_points.map((point, idx) => (
                                  <Typography key={idx} variant="body2" color="text.secondary" component="li">
                                    {point}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}

                      <ItemGoalReference
                        targetDescription={item.goal_target_description}
                        achievementCriteria={item.goal_achievement_criteria}
                        title={goalsApproved ? 'この項目の目標（確定）' : 'この項目の目標'}
                      />

                      <Divider sx={{ my: 2 }} />

                      {/* 上長評価 */}
                      {getManagerEvaluators().length > 0 && (
                        <Box mb={3}>
                          <Typography variant="subtitle2" gutterBottom>
                            上長評価
                          </Typography>
                          {getManagerEvaluators().map((evaluator) => {
                            const evalScore = getEvaluatorScore(evaluator.evaluator_id, item.item_id);
                            const evalComment = getEvaluatorComment(evaluator.evaluator_id, item.item_id);
                            return (
                              <Box
                                key={evaluator.evaluator_id}
                                sx={{
                                  mb: 2,
                                  p: 2,
                                  bgcolor: 'grey.50',
                                  borderRadius: 1,
                                  borderLeft: 3,
                                  borderColor: 'primary.main',
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Typography variant="body2" fontWeight="bold" color="primary">
                                    {evaluator.evaluator_name}
                                  </Typography>
                                  <Chip
                                    label={`重み: ${(evaluator.evaluation_weight * 100).toFixed(0)}%`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    評価点:
                                  </Typography>
                                  {evalScore !== null ? (
                                    <Chip
                                      label={`${evalScore}点`}
                                      color={getScoreColorUtil(evalScore)}
                                      size="small"
                                    />
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      (未入力)
                                    </Typography>
                                  )}
                                </Box>
                                {evalComment && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      コメント:
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                      {evalComment}
                                    </Typography>
                                  </Box>
                                )}
                                {!evalComment && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    (コメントなし)
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      {/* 部門長評価 */}
                      {getDeptHeadEvaluators().length > 0 && (
                        <Box mb={3}>
                          <Typography variant="subtitle2" gutterBottom>
                            部門長評価
                          </Typography>
                          {getDeptHeadEvaluators().map((evaluator) => {
                            const evalScore = getEvaluatorScore(evaluator.evaluator_id, item.item_id);
                            const evalComment = getEvaluatorComment(evaluator.evaluator_id, item.item_id);
                            return (
                              <Box
                                key={evaluator.evaluator_id}
                                sx={{
                                  mb: 2,
                                  p: 2,
                                  bgcolor: 'grey.50',
                                  borderRadius: 1,
                                  borderLeft: 3,
                                  borderColor: 'secondary.main',
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Typography variant="body2" fontWeight="bold" color="secondary">
                                    {evaluator.evaluator_name}
                                  </Typography>
                                  <Chip
                                    label={`重み: ${(evaluator.evaluation_weight * 100).toFixed(0)}%`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    評価点:
                                  </Typography>
                                  {evalScore !== null ? (
                                    <Chip
                                      label={`${evalScore}点`}
                                      color={getScoreColorUtil(evalScore)}
                                      size="small"
                                    />
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      (未入力)
                                    </Typography>
                                  )}
                                </Box>
                                {evalComment && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      コメント:
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                      {evalComment}
                                    </Typography>
                                  </Box>
                                )}
                                {!evalComment && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    (コメントなし)
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      {/* 評価点 */}
                      <Box mb={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle2">自己評価点（1-10点）</Typography>
                          <Chip
                            label={`${formData[item.item_id]?.first_evaluation_score || 6}点 - ${getShortScoreDescription(formData[item.item_id]?.first_evaluation_score || 6)}`}
                            color={getScoreColorUtil(formData[item.item_id]?.first_evaluation_score || 6)}
                            size="small"
                          />
                        </Box>
                        <Slider
                          value={formData[item.item_id]?.first_evaluation_score || 6}
                          onChange={(_, value) =>
                            handleInputChange(item.item_id, 'first_evaluation_score', value as number)
                          }
                          min={1}
                          max={10}
                          step={1}
                          marks
                          valueLabelDisplay="auto"
                          disabled={isSubmitted}
                        />
                        {/* 評価点の詳細説明 */}
                        <Box
                          sx={{
                            mt: 1,
                            p: 2,
                            bgcolor: getScoreDescription(formData[item.item_id]?.first_evaluation_score || 6).isStandard
                              ? 'info.50'
                              : 'grey.50',
                            borderRadius: 1,
                            borderLeft: 4,
                            borderColor: getScoreDescription(formData[item.item_id]?.first_evaluation_score || 6).isStandard
                              ? 'info.main'
                              : 'grey.300',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" display="block" fontWeight="bold" mb={0.5}>
                            評価点の意味：
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {getScoreDescription(formData[item.item_id]?.first_evaluation_score || 6).description}
                          </Typography>
                        </Box>
                      </Box>

                      {/* 評価の理由や特記事項 */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          評価の理由や特記事項（あれば）
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="評価の理由や特記事項があれば記入してください..."
                          value={formData[item.item_id]?.first_evaluation_comment || ''}
                          onChange={(e) =>
                            handleInputChange(item.item_id, 'first_evaluation_comment', e.target.value)
                          }
                          disabled={isSubmitted}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* アクションボタン */}
      <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleSaveDraft}
          disabled={saving || !canEditEvaluation}
        >
          一時保存
        </Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSubmit}
          disabled={
            saving ||
            !canEditEvaluation ||
            categories.length === 0
          }
        >
          自己評価を提出
        </Button>
      </Box>

      {/* 提出確認ダイアログ */}
      <Dialog open={submitDialogOpen} onClose={() => setSubmitDialogOpen(false)}>
        <DialogTitle>自己評価の提出</DialogTitle>
        <DialogContent>
          <Typography>
            自己評価を提出してもよろしいですか？
            <br />
            提出後は編集できなくなります。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialogOpen(false)} disabled={saving}>
            キャンセル
          </Button>
          <Button onClick={handleConfirmSubmit} variant="contained" disabled={saving}>
            {saving ? '提出中...' : '提出する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SelfEvaluationPage;
